/**
 * Demo data: a realistic fixture and a simulator that keeps it moving, so every page
 * can be built and shown without a hub. Enabled with ?mock=1 or `npm -w ui run dev:mock`.
 *
 * The simulator emits the same StatePatch / FeedEvent / AuditEntry messages the hub
 * sends over SSE, through the same store functions.
 */
import { ACP_AGENTS, ACTION_LABEL, AGENT_CHECKS_STAGE, CACHE_TTL_1H_MS, CACHE_TTL_5M_MS, LANDING_FATE, ORGUS_LAND_STAGE, PROJECT_PROTOCOL_ADAPTER, SESSION_CURRENT_START_EXT, SESSION_OWN_WORK_EXT, SESSION_USAGE_EXT, agentKindOf, agentSupports, askLastText, contextWindowOf, pipelineKey, sessionAsk } from '@orgus/protocol';
import type { SessionLiveUsage } from '@orgus/protocol';
import type {
  AgentRuntime,
  CodexSessionExt,
  ActionKind,
  ActionParamsMap,
  Activity,
  ActivityMarkKind,
  ActionResponse,
  AggregateState,
  AuditEntry,
  Checkout,
  FeedEvent,
  FeedEventKind,
  Id,
  Job,
  Machine,
  MachineReadiness,
  Pipeline,
  PipelineCapacity,
  PipelineItem,
  Project,
  ProjectSuggestion,
  Session,
  SessionStatus,
  StatePatch,
  Subagent,
  WorkflowAgent,
  WorkflowRun,
} from '@orgus/protocol';
import type { Entities, MachineSample } from './store.ts';
import { mockWatch } from '../features/watch/mock.ts';
import { mockMachineLoad } from '../features/machine-load/mock.ts';

/** A waiting session's whole last message: context first, choices, then the question (ADR 0056). */
const WEBHOOK_ASK = [
  'Webhook retries are covered for the happy path and for duplicate deliveries. One decision is open before I write the rest.',
  '',
  '**What changed**',
  '',
  '- Retries carry the event id as the idempotency key, so a replay never charges twice.',
  '- `handleRetry` backs off 1 s, 4 s and 16 s, and gives up after the fourth attempt.',
  '- The fixtures under `test/fixtures/stripe` are regenerated from the current API version.',
  '',
  'The retry test can run against Stripe test mode, which is slower and needs a key in CI, or against the recorded fixtures, which are fast but can drift from the live API.',
  '',
  '1. Use Stripe test mode in CI',
  '2. Use the recorded fixtures, refreshed weekly',
  '3. Use fixtures on every push and test mode nightly',
  '',
  'Which should the retry tests use?',
].join('\n');

/** A controlled session's ask with quick replies. */
const LAND_ASK = [
  'The request card renders in chat, and the inbox lists agent requests from every session. Tests and typecheck pass.',
  '',
  'Landing runs `npm run land`, which pushes to main.',
  '',
  '- Land it now',
  '- Open a pull request instead',
  '- Wait until the review is done',
  '',
  'How do you want to land it?',
].join('\n');

const S = 1000;
const MIN = 60 * S;
const H = 60 * MIN;
const GB = 1024 ** 3;

/** Deterministic PRNG so the fixture (and screenshots) are stable. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const MOCK_IDS = {
  studio: 'm-studio',
  mbp: 'm-mbp16',
  build: 'm-build01',
  qawl: 'p-7f3a91c2',
  orgus: 'p-0b44e8d1',
  billing: 'p-c19d2a07',
  docs: 'p-5ae0f6b3',
  mobile: 'p-3d81b7e4',
  gateway: 'p-9e26c5f0',
} as const;

const M = MOCK_IDS;

/**
 * The demo questions of ADR 0056 in ?mock=1: one asked over a day ago (an open question, under
 * Idle) and one a person dismissed (features/asks/mock.ts holds the dismissal).
 */
export const MOCK_ASKS = {
  openSession: 'e7b1c2d4-export-format',
  dismissedSession: 'f2a9d3e8-sdk-lockfile',
  dismissedAsk: 'f2a9d3e8-sdk-lockfile:demo-lockfile',
} as const;

const EXPORT_ASK = [
  'The nightly export writes about 4.2 GB of CSV. Parquet would be close to 600 MB and loads into the warehouse in a third of the time, but two downstream scripts read the CSV directly.',
  '',
  'Should the nightly export keep CSV, or move to Parquet?',
].join('\n');

const LOCKFILE_ASK = 'Bumped the SDK to 0.3.1 and the tests pass. Do you also want me to refresh the lockfile?';

/** The demo sessions, missions and requests that show approval outcomes (expired, denied) in ?mock=1. */
export const MOCK_BLOCKED = {
  expiredSession: 'd5e1c0b7-sdk-release',
  expiredMission: 'msn_demo_release',
  expiredApproval: 'apr_DEMO_PUBLISH',
  deniedSession: 'a8c3e6f4-ledger-backfill',
  deniedMission: 'msn_demo_ledger',
  deniedApproval: 'apr_DEMO_LEDGER',
} as const;

/** The demo failures (ADR 0059): a mission whose model does not exist (two runs), signed out, overloaded, a task that failed, and one from yesterday. */
export const MOCK_FAILED = {
  mission: 'msn_demo_add',
  modelRuns: ['f1a0c3e2-add-haiku-1', 'f1a0c3e2-add-haiku-2'],
  auth: 'a2b4c6d8-readme-auth',
  api: 'e3c5a7b9-store-overloaded',
  task: '7d9f1b3c-flaky-tests',
  old: '0b1d3f5a-old-probe',
  test: 'c4e6a8b0-probe-test',
  folder: 'C:/Users/Srika/AppData/Local/Temp/orgus-exp',
} as const;
const MOCK_MODEL_ERROR = "There's an issue with the selected model (claude-haiku-5). It may not exist or you may not have access to it. Run /model to pick a different model.";

// ---------------------------------------------------------------------------
// Fixture
// ---------------------------------------------------------------------------

/** Every kind a current agent can start: Claude Code, Codex, then each ACP agent (ADR 0049). */
const ALL_AGENT_KINDS = ['claude-code', 'codex', ...ACP_AGENTS.map((d) => d.kind)];

/**
 * Agent CLIs on a mock machine (null: not installed there). `acp` lists the ACP agents found
 * there with their versions; with it, every other ACP agent is reported as not installed, as a
 * current agent does.
 */
function runtimes(now: number, claude: string | null, codex: string | null, acp?: Record<string, string>): AgentRuntime[] {
  const r = (kind: string, version: string | null, path: string): AgentRuntime =>
    version ? { kind, installed: true, version, path, checkedAt: now - 4 * MIN } : { kind, installed: false, version: null, path: null, checkedAt: now - 4 * MIN };
  const out = [r('claude-code', claude, '/usr/local/bin/claude'), r('codex', codex, '/usr/local/bin/codex')];
  if (acp) for (const d of ACP_AGENTS) out.push(r(d.kind, acp[d.kind] ?? null, `/usr/local/bin/${d.launches[0]?.argv[0] ?? d.kind}`));
  return out;
}

/**
 * What a mock machine's agent found on it (R-ON-4): Claude Code and Codex with their sign-in
 * state, git, and repositories relative to the home folder (some watched, some not yet).
 */
function readiness(now: number, codex: 'signed-in' | 'signed-out', paths: string[]): MachineReadiness {
  return {
    checkedAt: now - 3 * MIN,
    harnesses: [
      { kind: 'claude-code', installed: true, version: '2.1.3 (Claude Code)', signIn: 'signed-in' },
      { kind: 'codex', installed: true, version: 'codex-cli 0.46.0', signIn: codex },
    ],
    git: { installed: true, version: '2.47.1', identityConfigured: true },
    repos: { count: paths.length, paths, truncated: false },
  };
}

/** The ext blob the Codex adapter writes. */
function codexExt(extra: Partial<CodexSessionExt>): Record<string, unknown> {
  const ext: CodexSessionExt = {
    native: 'turn',
    liveness: 'inferred',
    cliVersion: '0.46.0',
    originator: 'codex_cli_rs',
    source: 'cli',
    approvalPolicy: 'on-request',
    sandbox: 'workspace-write',
    effort: 'medium',
    reasoningTokens: 4_200,
    parentId: null,
    ...extra,
  };
  if (ext.liveness === 'inferred') ext.processCount ??= 2;
  return ext as unknown as Record<string, unknown>;
}

function machines(now: number): Machine[] {
  return [
    {
      id: M.studio,
      name: 'studio',
      os: 'windows',
      arch: 'x64',
      hostname: 'SRIKANTH-STUDIO',
      cpus: 24,
      memTotal: 64 * GB,
      memFree: 23.4 * GB,
      load: 0.46,
      agentVersion: '0.1.0',
      connectedAt: now - 6 * H,
      lastSeen: now - 2 * S,
      online: true,
      labels: ['primary', 'gpu'],
      agentKinds: ALL_AGENT_KINDS,
      runtimes: runtimes(now, '2.1.3', '0.46.0', { 'gemini-cli': '0.58.0', opencode: '0.15.2' }),
      readiness: readiness(now, 'signed-in', ['CascadeProjects/Qawl-Agents', 'CascadeProjects/dev-portal', 'CascadeProjects/status-page']),
    },
    {
      id: M.mbp,
      name: 'mbp-16',
      os: 'macos',
      arch: 'arm64',
      hostname: 'Srikanths-MacBook-Pro.local',
      cpus: 12,
      memTotal: 36 * GB,
      memFree: 14.1 * GB,
      load: 0.22,
      agentVersion: '0.1.0',
      connectedAt: now - 2 * H - 14 * MIN,
      lastSeen: now - 4 * S,
      online: true,
      labels: ['laptop'],
      agentKinds: ALL_AGENT_KINDS,
      runtimes: runtimes(now, '2.1.3', '0.46.0', { 'gemini-cli': '0.58.0' }),
      readiness: readiness(now, 'signed-out', ['code/qawl-agents', 'code/billing-service', 'code/docs-site', 'code/infra-terraform']),
    },
    {
      id: M.build,
      name: 'build-01',
      os: 'linux',
      arch: 'x64',
      hostname: 'build-01.lan',
      cpus: 32,
      memTotal: 128 * GB,
      memFree: 101 * GB,
      load: 0.08,
      agentVersion: '0.0.9',
      connectedAt: null,
      lastSeen: now - 3 * H - 12 * MIN,
      online: false,
      labels: ['ci', 'headless'],
      agentKinds: ['claude-code', 'codex'],
      runtimes: runtimes(now, '2.0.9', null),
    },
  ];
}

function projects(): Project[] {
  return [
    {
      id: M.qawl,
      name: 'Qawl-Agents',
      remoteUrl: 'https://github.com/qawl-ai/qawl-agents.git',
      defaultBranch: 'main',
      adapters: ['generic-git', 'qawl-land'],
    },
    {
      id: M.orgus,
      name: 'orgus',
      remoteUrl: 'git@github.com:srikanth/orgus.git',
      defaultBranch: 'main',
      adapters: ['generic-git'],
    },
    {
      id: M.billing,
      name: 'billing-service',
      remoteUrl: 'https://github.com/acme/billing-service.git',
      defaultBranch: 'main',
      adapters: ['generic-git'],
    },
    { id: M.docs, name: 'docs-site', remoteUrl: null, defaultBranch: 'main', adapters: ['generic-git'] },
    { id: M.mobile, name: 'mobile-app', remoteUrl: 'https://github.com/acme/mobile-app.git', defaultBranch: 'main', adapters: ['generic-git'] },
    { id: M.gateway, name: 'api-gateway', remoteUrl: 'https://github.com/acme/api-gateway.git', defaultBranch: 'main', adapters: ['generic-git'] },
  ];
}

function checkouts(now: number): Checkout[] {
  const c = (
    id: string,
    projectId: Id,
    machineId: Id,
    path: string,
    kind: Checkout['kind'],
    branch: string | null,
    head: string,
    headSubject: string,
    ahead: number,
    behind: number,
    dirty: number,
    updatedAgo: number,
    error?: string,
  ): Checkout => ({ id, projectId, machineId, path, kind, branch, head, headSubject, ahead, behind, dirty, updatedAt: now - updatedAgo, error });
  return [
    c('c-qawl-main', M.qawl, M.studio, 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'main', 'main', '9577348340a1c2d3e4f5a6b7c8d9e0f1a2b3c4d5', 'feat(approvals): the request card in chat, and one inbox', 0, 2, 21, 4 * S),
    c('c-qawl-approvals', M.qawl, M.studio, 'C:/tmp/qawl-wt-approvals', 'worktree', 'feat/approvals-inbox', 'd7036a8a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e', 'feat(approvals): the right person answers', 3, 0, 4, 6 * S),
    c('c-qawl-memory', M.qawl, M.studio, 'C:/tmp/qawl-wt-memory', 'worktree', 'feat/memory-operator', 'a5c136511b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e', 'feat(memory): the mind panel, phase 1a', 7, 1, 0, 8 * S),
    c('c-qawl-lander', M.qawl, M.studio, 'C:/tmp/qawl-lander', 'worktree', 'lander', '23664a50a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6', 'feat(permissions): the grid API at every scope', 0, 0, 0, 5 * S),
    c('c-qawl-voice', M.qawl, M.studio, 'C:/tmp/qawl-wt-voice-latency', 'worktree', 'fix/voice-latency', 'ce4421387a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d', 'wip: measure mint time per provider', 2, 14, 6, 9 * S),
    c('c-qawl-mbp', M.qawl, M.mbp, '/Users/srikanth/code/qawl-agents', 'main', 'main', '23664a50a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6', 'feat(permissions): the grid API at every scope', 0, 5, 0, 7 * S),
    c('c-fd-main', M.orgus, M.studio, 'C:/Users/Srika/CascadeProjects/dev-portal', 'main', 'main', '0000000000000000000000000000000000000000', 'No commits yet', 0, 0, 38, 3 * S),
    c('c-bill-mbp', M.billing, M.mbp, '/Users/srikanth/code/billing-service', 'main', 'main', '4be0c3a71d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6a', 'fix(invoices): round tax per line, not per invoice', 0, 0, 0, 6 * S),
    c('c-bill-retry', M.billing, M.mbp, '/Users/srikanth/code/billing-service-wt/stripe-retry', 'worktree', 'feat/stripe-retry', '8c1f2e3d4c5b6a7980a1b2c3d4e5f6a7b8c9d0e1', 'feat(webhooks): idempotency keys for retries', 2, 0, 3, 6 * S),
    c('c-bill-build', M.billing, M.build, '/srv/checkouts/billing-service', 'main', 'main', '3ad9e8f7a6b5c4d3e2f1a0b9c8d7e6f5a4b3c2d1', 'chore(deps): bump stripe to 18.2', 0, 3, 0, 3 * H + 12 * MIN),
    c('c-docs-mbp', M.docs, M.mbp, '/Users/srikanth/code/docs-site', 'main', 'main', '61f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b4a3f2', 'docs: v2 install steps', 1, 0, 1, 7 * S),
  ];
}

const tokens = (input: number, output: number, cacheRead: number) => ({ input, output, cacheRead });

function sub(
  sessionId: Id,
  n: number,
  type: string,
  description: string,
  status: Subagent['status'],
  startedAgo: number,
  durMs: number | null,
  toolCalls: number,
  lastText: string,
  now: number,
): Subagent {
  return {
    id: `${sessionId}-a${n}`,
    sessionId,
    type,
    description,
    status,
    startedAt: now - startedAgo,
    endedAt: durMs == null ? null : now - startedAgo + durMs,
    toolCalls,
    lastText,
  };
}

/**
 * Activity (module timeline): bursts as [started minutes ago, minutes long], oldest first, and
 * marks as [minutes ago, kind, detail].
 */
function bursts(now: number, spans: [number, number][], marks: [number, ActivityMarkKind, string][] = [], busy = false): Activity {
  const out = spans.map(([ago, len]) => ({ from: now - ago * MIN, to: now - ago * MIN + len * MIN }));
  return {
    spans: out,
    marks: marks.map(([ago, kind, detail]) => ({ at: now - ago * MIN, kind, detail })),
    activeMs: out.reduce((n, x) => n + x.to - x.from, 0),
    lastAt: out[out.length - 1]?.to ?? now,
    busy,
  };
}

/** Workflow agents: [phase, label, status, started minutes ago, minutes taken (null = running), tool calls, last text, worktree, activity]. */
type AgentSpec = [string, string, WorkflowAgent['status'], number, number | null, number, string, string | null, Activity?];
function flowAgents(runId: Id, now: number, specs: AgentSpec[]): WorkflowAgent[] {
  const agents = specs.map(([phase, label, status, ago, took, toolCalls, lastText, worktree, activity], i): WorkflowAgent => ({
    id: `${runId}-g${i + 1}`,
    label,
    phase,
    status,
    startedAt: now - ago * 60_000,
    endedAt: took == null ? null : now - ago * 60_000 + took * 60_000,
    toolCalls,
    lastText,
    worktree,
    ...(activity ? { activity } : {}),
  }));
  // A label that runs again after its earlier agent ended is a later attempt (as the agent reports it).
  const last = new Map<string, number>();
  agents.forEach((a, i) => {
    const prev = last.get(a.label);
    last.set(a.label, i);
    if (prev === undefined) return;
    const p = agents[prev]!;
    if (p.endedAt === null || p.endedAt > a.startedAt) return;
    p.attempt = p.attempt ?? 1;
    p.supersededBy = a.id;
    a.attempt = p.attempt + 1;
  });
  return agents;
}

/**
 * Demo Session.ext.usage (ADR 0057): a context fill, cache writes and the last request, so the
 * drawer's Usage group shows warm, cold, 1 hour and nearly full windows.
 */
function withLiveUsage(s: Session, now: number): Session {
  if (agentKindOf(s) !== 'claude-code' || s.tokens.cacheRead <= 0) return s;
  const h = [...s.id].reduce((n, c) => (n * 31 + c.charCodeAt(0)) % 9973, 7);
  const oneHour = h % 3 === 0;
  const fill = s.id.startsWith('a4e2b9c7') ? 0.86 : 0.12 + (h % 55) / 100;
  const window = contextWindowOf(s.model) ?? 200_000;
  const contextTokens = Math.round(window * fill);
  const cacheWrite = Math.round(s.tokens.input * 0.4);
  const cacheWrite1h = oneHour ? cacheWrite : 0;
  const lastRequestAt = s.status === 'working' ? now - 20 * S : s.lastActivity;
  // Requests since the last commit, so the continue or fresh line (ADR 0062 decision 2a) has a pace to measure.
  const calls = 4 + (h % 40);
  const progress = { at: s.lastActivity - 30 * 60 * S, kind: 'commit' as const, since: { [s.model ?? 'claude-opus-5-5']: { input: calls * 10, output: calls * 900, cacheRead: calls * contextTokens, cacheWrite: calls * 2200, cacheWrite1h: oneHour ? calls * 2200 : 0, calls } } };
  const usage: SessionLiveUsage = { cacheWrite, cacheWrite5m: cacheWrite - cacheWrite1h, cacheWrite1h, lastRequestAt, cacheTtlMs: oneHour ? CACHE_TTL_1H_MS : CACHE_TTL_5M_MS, contextTokens, contextWindow: contextWindowOf(s.model, contextTokens), progress };
  return { ...s, ext: { ...s.ext, [SESSION_USAGE_EXT]: usage } };
}

function sessions(now: number): Session[] {
  return sessionList(now).map((s) => withLiveUsage(s, now));
}

function sessionList(now: number): Session[] {
  const base = (
    id: Id,
    machineId: Id,
    projectId: Id | null,
    checkoutId: Id | null,
    cwd: string,
    title: string,
    status: SessionStatus,
    startedAgo: number,
    activeAgo: number,
    extra: Partial<Session> = {},
  ): Session => ({
    id,
    machineId,
    projectId,
    checkoutId,
    cwd,
    title,
    status,
    pid: status === 'ended' ? null : 10_000 + Math.floor(id.length * 977 + startedAgo / 1000) % 50_000,
    model: 'claude-opus-5',
    permissionMode: 'default',
    startedAt: now - startedAgo,
    lastActivity: now - activeAgo,
    turns: 6,
    toolCalls: 24,
    tokens: tokens(42_000, 9_800, 310_000),
    lastText: '',
    subagents: [],
    workflows: [],
    origin: 'interactive',
    ...extra,
  });

  const s1 = 'b1f4c2e0-approvals';
  const s4 = 'e7a90d13-foundation';
  return [
    // ORGU-132: its turn ended while its own tests run; woken twice since it first started.
    base('c3d8e1f2-own-work', M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Session drawer: say Turn ended, not Finished', 'working', 21 * H + 27 * MIN, 6 * MIN, {
      turns: 96,
      toolCalls: 412,
      origin: 'orgus',
      lastText: 'The tests run in the background; I submit when they pass.',
      ext: {
        [SESSION_OWN_WORK_EXT]: { turnEndedAt: now - 6 * MIN, work: [{ kind: 'monitor', what: 'pnpm test:affected', at: now - 5 * MIN, due: null }] },
        [SESSION_CURRENT_START_EXT]: now - 23 * MIN,
      },
    }),
    base(s1, M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Approvals: the right person answers, and may say do not ask again', 'working', 2 * H + 10 * MIN, 8 * S, {
      turns: 31,
      toolCalls: 214,
      // A long transcript the agent has read only the tail of so far: counts are lower bounds.
      partial: true,
      subagentsTotal: 12,
      tokens: tokens(1_284_000, 96_400, 18_900_000),
      permissionMode: 'bypassPermissions',
      lastText: 'The approval routing test passes. Wiring the inbox count into the rail badge next, then running the scope check.',
      subagents: [
        sub(s1, 1, 'Explore', 'Map every caller of requireScope', 'done', 1 * H + 50 * MIN, 6 * MIN, 41, 'Found 38 call sites; 3 read orgId from the body directly.', now),
        sub(s1, 2, 'general-purpose', 'Port the request card to the chat stream', 'running', 22 * MIN, null, 57, 'Rendering the card inline; adding the Allow once / Always allow pair.', now),
        sub(s1, 3, 'code-reviewer', 'Review the approvals diff for scope leaks', 'running', 6 * MIN, null, 12, 'Checking the list predicate matches the write guard.', now),
      ],
      workflows: [
        {
          id: `${s1}-w1`,
          sessionId: s1,
          name: 'land-and-verify',
          description: 'Verify the approvals batch and submit it to the landing queue',
          status: 'running',
          phases: [
            { title: 'Typecheck', agents: 1, done: 1 },
            { title: 'Unit tests', agents: 4, done: 3 },
            { title: 'Scope check', agents: 0, done: 0 },
            { title: 'Submit', agents: 0, done: 0 },
          ],
          startedAt: now - 9 * MIN,
          endedAt: null,
          agents: flowAgents(`${s1}-w1`, now, [
            ['Typecheck', 'typecheck', 'done', 9, 2, 6, 'No type errors in 412 files.', null],
            ['Unit tests', 'tests:hub', 'done', 7, 3, 9, '128 tests pass.', null],
            ['Unit tests', 'tests:agent', 'done', 7, 4, 11, '96 tests pass.', null],
            ['Unit tests', 'tests:web', 'done', 7, 5, 14, '211 tests pass.', null],
            ['Unit tests', 'tests:e2e', 'running', 7, null, 23, 'Approvals flow: 9 of 14 specs pass so far.', 'C:/tmp/qawl-wt-e2e'],
          ]),
        },
      ],
    }),
    // Subagents that worked in bursts over seven hours, resumed by the coordinator's messages.
    base('5d212fa9-held-extras', M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Held approvals: park to the inbox, route to the approver, resume from the held step', 'working', 7 * H, 20 * S, {
      turns: 24,
      toolCalls: 318,
      tokens: tokens(2_140_000, 160_000, 31_000_000),
      permissionMode: 'bypassPermissions',
      lastText: 'Applying the remaining picks onto the site-lists base, then the full test run and the submit.',
      subagents: [
        {
          ...sub('5d212fa9-held-extras', 1, 'general-purpose', 'Build held-approval extras', 'running', 410 * MIN, null, 412, 'Continuing the cherry-pick onto the site-lists base.', now),
          activity: bursts(
            now,
            [[410, 60], [328, 84], [226, 70], [137, 24], [92, 27], [25, 25]],
            [[328, 'resume', 'message from the coordinator'], [226, 'resume', 'message from the coordinator'], [137, 'resume', 'message from the coordinator'], [92, 'resume', 'message from the coordinator'], [25, 'resume', 'message from the coordinator'], [17, 'compact', 'automatic']],
            true,
          ),
        },
        {
          ...sub('5d212fa9-held-extras', 2, 'general-purpose', 'Extension: parked browser holds', 'done', 405 * MIN, 388 * MIN, 188, 'Extension conflicts resolved: 584 of 584 tests pass, typecheck and eslint clean.', now),
          activity: bursts(now, [[405, 28], [325, 25], [155, 8], [23, 6]], [[325, 'resume', 'message from another agent'], [155, 'resume', 'message from another agent'], [23, 'resume', 'message from another agent']]),
        },
        {
          ...sub('5d212fa9-held-extras', 3, 'general-purpose', 'Nexus review fixes', 'running', 95 * MIN, null, 64, 'Running the full api suite: 427 of 428 files so far.', now),
          activity: bursts(now, [[95, 55]], [], true),
        },
      ],
    }),
    base('c3d8a7f1-request-card', M.studio, M.qawl, 'c-qawl-approvals', 'C:/tmp/qawl-wt-approvals', 'Wire the request card into chat and add one inbox for agent requests', 'waiting', 1 * H + 5 * MIN, 3 * MIN, {
      turns: 14,
      toolCalls: 88,
      tokens: tokens(512_000, 41_200, 6_400_000),
      lastText: askLastText(LAND_ASK),
      ext: { asks: sessionAsk('c3d8a7f1-request-card', 'demo-land', LAND_ASK, now - 3 * MIN, 'transcript') },
    }),
    base('a4e2b9c7-memory', M.studio, M.qawl, 'c-qawl-memory', 'C:/tmp/qawl-wt-memory', 'Memory page redesign, phase 1a: operator with a mind', 'working', 48 * MIN, 21 * S, {
      turns: 9,
      toolCalls: 63,
      tokens: tokens(388_000, 30_100, 4_100_000),
      model: 'claude-sonnet-5',
      lastText: 'The mind panel now collapses below 560 px. Writing the edit history list.',
      subagents: [sub('a4e2b9c7-memory', 1, 'Plan', 'Plan the areas v4 migration', 'running', 12 * MIN, null, 18, 'Drafting the migration order.', now)],
      workflows: [
        {
          id: 'a4e2b9c7-memory-w1',
          sessionId: 'a4e2b9c7-memory',
          name: 'memory-research',
          description: 'Compare four memory UIs and pick a direction',
          status: 'done',
          phases: [
            { title: 'Collect', agents: 4, done: 4 },
            { title: 'Compare', agents: 2, done: 2 },
            { title: 'Recommend', agents: 1, done: 1 },
          ],
          startedAt: now - 46 * MIN,
          endedAt: now - 31 * MIN,
          agents: flowAgents('a4e2b9c7-memory-w1', now, [
            ['Collect', 'collect:notion', 'done', 46, 5, 18, 'Notion keeps memory as pages with backlinks.', null],
            ['Collect', 'collect:mem0', 'done', 46, 6, 21, 'Mem0 scores memories by recency and use.', null],
            ['Collect', 'collect:letta', 'done', 46, 7, 16, 'Letta shows core memory as editable blocks.', null],
            ['Collect', 'collect:chat', 'done', 46, 4, 12, 'The chat app lists memories as plain sentences.', null],
            ['Compare', 'compare:editing', 'done', 39, 4, 9, 'Inline editing wins for trust.', null],
            ['Compare', 'compare:recall', 'done', 39, 5, 10, 'Recency plus pinning covers most recall.', null],
            ['Recommend', 'recommend', 'done', 34, 3, 5, 'Go with editable blocks plus a recency list.', null],
          ]),
        },
      ],
    }),
    base(s4, M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Build Orgus: protocol, design system and app shell', 'working', 1 * H + 32 * MIN, 3 * S, {
      turns: 12,
      toolCalls: 140,
      tokens: tokens(860_000, 120_500, 9_700_000),
      permissionMode: 'bypassPermissions',
      lastText: 'Foundation is in. Six builders are working on pages in parallel; review starts when they finish.',
      subagents: [
        sub(s4, 1, 'general-purpose', 'Foundation: protocol, design system, shell', 'done', 1 * H + 28 * MIN, 41 * MIN, 166, 'Typecheck, tests and build pass.', now),
        sub(s4, 2, 'general-purpose', 'Agent: collectors and adapters', 'running', 44 * MIN, null, 92, 'Incremental transcript reader done; mapping live processes.', now),
        sub(s4, 3, 'general-purpose', 'Hub: registry, API and SSE', 'running', 44 * MIN, null, 77, 'Join tokens and scrypt secrets done; writing the audit log.', now),
        sub(s4, 4, 'general-purpose', 'Overview page', 'running', 43 * MIN, null, 58, 'Needs you list renders; building project cards.', now),
        sub(s4, 5, 'general-purpose', 'Pipelines page', 'failed', 43 * MIN, 19 * MIN, 31, 'Stopped: the stage lane needed a protocol change.', now),
        sub(s4, 6, 'general-purpose', 'Pipelines page (retry)', 'running', 23 * MIN, null, 40, 'Items animate between stages.', now),
      ],
      workflows: [
        {
          id: `${s4}-w1`,
          sessionId: s4,
          name: 'orgus-build',
          description: 'Foundation, then parallel builders, then review',
          status: 'running',
          phases: [
            { title: 'Foundation', agents: 1, done: 1 },
            { title: 'Builders', agents: 6, done: 2 },
            { title: 'Review', agents: 2, done: 0 },
          ],
          startedAt: now - 1 * H - 30 * MIN,
          endedAt: null,
          agents: flowAgents(`${s4}-w1`, now, [
            ['Foundation', 'foundation', 'done', 90, 41, 166, 'Typecheck, tests and build pass.', null],
            ['Builders', 'build:overview', 'done', 48, 22, 61, 'Overview renders the needs-you list and project cards.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-1'],
            ['Builders', 'build:sessions', 'done', 48, 31, 77, 'Grouped list, filters and the drawer are in.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-2'],
            ['Builders', 'build:pipelines', 'failed', 48, 19, 31, 'Stopped: the stage lane needed a protocol change.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-3'],
            ['Builders', 'build:agents', 'running', 48, null, 88, 'Workflow rows open into one lane per agent.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-4'],
            ['Builders', 'build:machines', 'running', 47, null, 54, 'Capacity sparklines render; wiring the join token dialog.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-5'],
            // Ended its turn 15 min ago and heard nothing since, its host still alive: looks stuck.
            ['Builders', 'build:audit', 'running', 47, null, 42, 'Paging the audit log by cursor.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-6', bursts(now, [[47, 32]])],
            ['Builders', 'build:pipelines', 'running', 26, null, 23, 'The protocol change landed; wiring the stage lane again.', 'C:/Users/Srika/CascadeProjects/dev-portal/.claude/worktrees/build-3'],
          ]),
        },
      ],
    }),
    base('f0c9e1a2-sessions-page', M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Sessions page: grouped list, filters and the detail drawer', 'working', 26 * MIN, 12 * S, {
      turns: 4,
      toolCalls: 46,
      tokens: tokens(201_000, 22_000, 1_900_000),
      origin: 'orgus',
      permissionMode: 'acceptEdits',
      lastText: 'Grouping by project then machine; the drawer timeline renders subagents as nested lanes.',
      subagents: [sub('f0c9e1a2-sessions-page', 1, 'Explore', 'Read the transcript formats in reference', 'done', 24 * MIN, 4 * MIN, 22, 'Prompts, tool calls and subagent metas decoded.', now)],
    }),
    base('7b3e5d20-stripe-retry', M.mbp, M.billing, 'c-bill-mbp', '/Users/srikanth/code/billing-service', 'Retry failed Stripe webhooks with idempotency keys', 'idle', 3 * H, 18 * MIN, {
      turns: 22,
      toolCalls: 131,
      tokens: tokens(640_000, 51_000, 7_200_000),
      model: 'claude-sonnet-5',
      lastText: 'Done. Retries now carry the event id as the idempotency key. Want me to add a backfill for last week?',
    }),
    base('9d1a4c6e-webhook-tests', M.mbp, M.billing, 'c-bill-retry', '/Users/srikanth/code/billing-service-wt/stripe-retry', 'Add integration tests for webhook retries', 'waiting', 52 * MIN, 7 * MIN, {
      turns: 8,
      toolCalls: 39,
      tokens: tokens(180_000, 16_400, 1_100_000),
      model: 'claude-sonnet-5',
      // A turn that ended on a question with choices (ADR 0056): the whole message is ext.asks.
      lastText: askLastText(WEBHOOK_ASK),
      ext: { asks: sessionAsk('9d1a4c6e-webhook-tests', 'demo-ask', WEBHOOK_ASK, now - 7 * MIN, 'transcript') },
    }),
    // ADR 0056: a question nobody answered for over a day reads "Open question" under Idle.
    base(MOCK_ASKS.openSession, M.mbp, M.docs, 'c-docs-mbp', '/Users/srikanth/code/docs-site', 'Move the nightly export to a smaller format', 'waiting', 31 * H, 30 * H, {
      turns: 6,
      toolCalls: 24,
      tokens: tokens(120_000, 9_800, 700_000),
      model: 'claude-sonnet-5',
      lastText: askLastText(EXPORT_ASK),
      ext: { asks: sessionAsk(MOCK_ASKS.openSession, 'demo-export', EXPORT_ASK, now - 30 * H, 'transcript') },
    }),
    // ADR 0056: a question a person dismissed ("No answer needed"); it stays in Done with Undo.
    base(MOCK_ASKS.dismissedSession, M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Bump the SDK and rerun the tests', 'waiting', 2 * H, 95 * MIN, {
      turns: 4,
      toolCalls: 18,
      tokens: tokens(90_000, 6_200, 500_000),
      lastText: LOCKFILE_ASK,
      ext: { asks: sessionAsk(MOCK_ASKS.dismissedSession, 'demo-lockfile', LOCKFILE_ASK, now - 95 * MIN, 'stop-hook') },
    }),
    base('2e8f7a91-docs-v2', M.mbp, M.docs, 'c-docs-mbp', '/Users/srikanth/code/docs-site', 'Rewrite the getting started guide for v2', 'ended', 5 * H, 2 * H + 4 * MIN, {
      turns: 17,
      toolCalls: 72,
      tokens: tokens(310_000, 44_000, 2_600_000),
      lastText: 'The guide is rewritten and the screenshots are updated.',
    }),
    // Approval outcomes (R-HL-3): a headless mission session stopped by an approval nobody
    // answered, and one stopped by a denial. Their requests are in features/approvals/mock.ts.
    base(MOCK_BLOCKED.expiredSession, M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Release the SDK: version bump, build and publish', 'ended', 1 * H + 5 * MIN, 39 * MIN, {
      turns: 7,
      toolCalls: 41,
      tokens: tokens(240_000, 21_000, 2_100_000),
      origin: 'orgus',
      lastText: 'Stopped: the publish needs an approval and nobody answered. Version 0.3.0 is built and tagged locally; only the publish is left.',
    }),
    base(MOCK_BLOCKED.deniedSession, M.mbp, M.billing, 'c-bill-mbp', '/Users/srikanth/code/billing-service', 'Backfill the ledger table for March', 'ended', 1 * H + 20 * MIN, 22 * MIN, {
      turns: 9,
      toolCalls: 57,
      tokens: tokens(310_000, 26_000, 3_000_000),
      model: 'claude-sonnet-5',
      origin: 'orgus',
      lastText: 'Ran the backfill on staging as you asked: 1,284 refunds and 1,284 ledger rows, and the March totals reconcile.',
    }),
    // Failures (ADR 0059): each says its cause, and two runs of one mission fold into one row.
    ...MOCK_FAILED.modelRuns.map((id, i) =>
      base(id, M.studio, null, null, MOCK_FAILED.folder, `[${MOCK_FAILED.mission}] add() subtracts instead of adding`, 'ended', (3 - i) * H + 3_000, (3 - i) * H, {
        turns: 1,
        toolCalls: 0,
        tokens: tokens(0, 0, 0),
        model: null,
        origin: 'orgus',
        lastText: MOCK_MODEL_ERROR,
      }),
    ),
    base(MOCK_FAILED.auth, M.mbp, M.billing, 'c-bill-mbp', '/Users/srikanth/code/billing-service', 'Update the README badges', 'ended', 52 * MIN, 50 * MIN, {
      turns: 1,
      toolCalls: 0,
      tokens: tokens(0, 0, 0),
      lastText: 'Invalid API key · Please run /login',
    }),
    base(MOCK_FAILED.api, M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Refactor the session store into one module', 'ended', 48 * MIN, 21 * MIN, {
      turns: 4,
      toolCalls: 19,
      tokens: tokens(120_000, 8_400, 900_000),
      lastText: 'API Error: 529 {"type":"error","error":{"type":"overloaded_error","message":"Overloaded"}}',
    }),
    base(MOCK_FAILED.task, M.build, M.billing, 'c-bill-build', '/srv/checkouts/billing-service', 'Make the webhook integration tests pass on CI', 'ended', 2 * H, 1 * H + 10 * MIN, {
      turns: 8,
      toolCalls: 63,
      tokens: tokens(280_000, 19_000, 2_400_000),
      origin: 'orgus',
      lastText: 'Two of the 14 integration tests still fail: the retry test times out on the CI runner after 30 s.',
      ext: {
        failure: {
          cause: 'task',
          summary: 'Two of the 14 integration tests still fail; the retry test times out on the CI runner.',
          detail: 'Two of the 14 integration tests still fail: the retry test times out on the CI runner after 30 s.',
          at: now - 1 * H - 10 * MIN,
        },
      },
    }),
    // A probe a test agent launched (ADR 0059 decision 4): only under the Tests filter.
    base(MOCK_FAILED.test, M.studio, null, null, MOCK_FAILED.folder, 'Reply with only the word one.', 'ended', 2 * H + 4_000, 2 * H, {
      turns: 1,
      toolCalls: 0,
      tokens: tokens(9, 1, 0),
      model: 'claude-haiku-5-5',
      origin: 'orgus',
      lastText: 'one',
      ext: { testRun: true },
    }),
    base(MOCK_FAILED.old, M.studio, null, null, MOCK_FAILED.folder, 'Reply with only the word one.', 'ended', 30 * H + 4_000, 30 * H, {
      turns: 1,
      toolCalls: 0,
      tokens: tokens(0, 0, 0),
      model: 'claude-haiku-5-5',
      origin: 'orgus',
      lastText: 'API Error: 500 {"type":"error","error":{"type":"api_error","message":"Internal server error"}}',
    }),
    base('5c2b8e4d-voice-latency', M.mbp, M.qawl, 'c-qawl-mbp', '/Users/srikanth/code/qawl-agents', 'Investigate voice mint latency on Gemini Live', 'ended', 7 * H, 5 * H, {
      turns: 26,
      toolCalls: 188,
      tokens: tokens(990_000, 70_000, 12_000_000),
      lastText: 'Mint takes 3 to 4 s because the token request waits on the config fetch. Notes are in the PR.',
      subagents: [
        sub('5c2b8e4d-voice-latency', 1, 'Explore', 'Trace the mint request path', 'done', 6 * H + 40 * MIN, 9 * MIN, 35, 'Three sequential round trips before the mint.', now),
        sub('5c2b8e4d-voice-latency', 2, 'general-purpose', 'Measure mint time per provider', 'failed', 6 * H, 21 * MIN, 49, 'The Azure probe timed out after 30 s.', now),
      ],
    }),
    base('8a0d3f6b-pricing', M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Fix $0 run pricing and backfill the usage rows', 'ended', 27 * H, 25 * H, {
      turns: 19,
      toolCalls: 120,
      tokens: tokens(702_000, 58_000, 8_800_000),
      lastText: 'Deployed. Thread totals now re-price from usage rows every turn.',
    }),
    base('1f6e2a9c-nightly-deps', M.build, M.billing, 'c-bill-build', '/srv/checkouts/billing-service', 'Nightly dependency upgrade', 'ended', 4 * H, 3 * H + 13 * MIN, {
      turns: 3,
      toolCalls: 28,
      tokens: tokens(64_000, 7_100, 300_000),
      model: 'claude-haiku-5-5',
      origin: 'orgus',
      lastText: 'Bumped stripe to 18.2. Tests pass.',
    }),
    base('4d7c1b8e-artifact-erase', M.studio, M.qawl, 'c-qawl-main', 'C:/Users/Srika/CascadeProjects/Qawl-Agents', 'Review the artifact lifecycle: org erase leaves objects in R2', 'idle', 1 * H + 40 * MIN, 25 * MIN, {
      turns: 11,
      toolCalls: 67,
      tokens: tokens(420_000, 35_000, 5_000_000),
      lastText: 'Summary posted. The erase job skips the artifacts prefix; the fix is a second list-and-delete pass.',
    }),
    base('6b9f0e3a-openapi', M.mbp, M.docs, 'c-docs-mbp', '/Users/srikanth/code/docs-site', 'Generate the API reference from the OpenAPI spec', 'working', 14 * MIN, 5 * S, {
      turns: 5,
      toolCalls: 33,
      tokens: tokens(150_000, 19_800, 800_000),
      model: 'claude-sonnet-5',
      lastText: 'Generating one page per tag; 14 of 22 written.',
      subagents: [sub('6b9f0e3a-openapi', 1, 'general-purpose', 'Write examples for each endpoint', 'running', 9 * MIN, null, 21, 'Examples for invoices and customers done.', now)],
    }),
    base('0199f6b0-codex-invoices', M.mbp, M.billing, 'c-bill-retry', '/Users/srikanth/code/billing-service-wt/stripe-retry', 'Backfill the missing invoice PDFs for September', 'working', 21 * MIN, 6 * S, {
      agentKind: 'codex',
      state: 'working',
      pid: null,
      model: 'gpt-5-codex',
      permissionMode: 'workspace-write',
      turns: 3,
      toolCalls: 41,
      tokens: tokens(96_000, 12_400, 610_000),
      lastText: 'Found 212 invoices without a PDF. Rendering them in batches of 25.',
      ext: codexExt({}),
    }),
    base('0199f1a2-codex-fixtures', M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Write rollout fixtures for the Codex adapter tests', 'idle', 48 * MIN, 9 * MIN, {
      agentKind: 'codex',
      state: 'idle',
      pid: null,
      model: 'gpt-5.2-codex',
      permissionMode: 'read-only',
      turns: 4,
      toolCalls: 18,
      tokens: tokens(58_000, 7_300, 240_000),
      lastText: 'The legacy and paginated fixtures are written, with one for the early bare format.',
      ext: codexExt({ native: 'complete', sandbox: 'read-only', approvalPolicy: 'never', source: 'vscode', originator: 'codex_vscode', cliVersion: '0.153.0' }),
    }),
    // A headless Gemini CLI mission run over ACP: its drawer shows reasoning steps and a Markdown reply.
    base('3f1d9a7c-gemini-landing', M.studio, M.orgus, 'c-fd-main', 'C:/Users/Srika/CascadeProjects/dev-portal', 'Fix the failed landing of gemini-usage', 'ended', 3 * H, 2 * H + 35 * MIN, {
      agentKind: 'gemini-cli',
      state: 'idle',
      model: 'gemini-3-pro',
      permissionMode: 'acceptEdits',
      origin: 'orgus',
      turns: 1,
      toolCalls: 14,
      tokens: tokens(88_000, 6_200, 0),
      lastText: 'The landing failed because cached tokens were counted twice. Fixed in otel-file.ts and landed again.',
    }),
    base('0199e0c0-codex-stripe', M.build, M.billing, 'c-bill-build', '/srv/checkouts/billing-service', 'Upgrade the Stripe SDK to v18 and fix the types', 'ended', 6 * H, 5 * H + 40 * MIN, {
      agentKind: 'codex',
      state: 'failed',
      model: 'gpt-5-codex',
      permissionMode: 'workspace-write',
      origin: 'orgus',
      turns: 1,
      toolCalls: 9,
      tokens: tokens(31_000, 2_100, 0),
      lastText: 'The turn failed: unexpected status 429 Too Many Requests.',
      ext: codexExt({ native: 'failed', liveness: 'none', source: 'exec', originator: 'codex_exec', approvalPolicy: 'never' }),
    }),
    base('0e5a7c2f-pwsh', M.studio, null, null, 'C:/Users/Srika', 'Quick question about PowerShell profile load time', 'ended', 30 * H, 29 * H + 50 * MIN, {
      turns: 2,
      toolCalls: 3,
      tokens: tokens(12_000, 1_400, 0),
      model: 'claude-haiku-5-5',
      lastText: 'Your profile imports three modules eagerly; lazy-load them with a proxy function.',
    }),
  ];
}

const QAWL_STAGES = [
  { id: 'queued', title: 'Queued' },
  { id: 'verifying', title: 'Verifying' },
  { id: 'pushing', title: 'Pushing' },
  { id: 'deploying', title: 'Deploying' },
  { id: 'landed', title: 'Landed' },
];

/**
 * The verify pool as on the real machine on 17 Oct: the landing queue's verify holds 28 of 30
 * test workers (all 24 kept for it, and 4 more), 2 are free, and 18 of its tasks wait.
 */
function qawlPool(now: number): PipelineCapacity {
  const RUN = 58200;
  const since = now - 9 * MIN;
  const held: [string, number][] = [
    ['console:test', 13],
    ['runtime:test', 7],
    ['nexus:test', 6],
    ['console:typecheck', 2],
  ];
  const waiting = ['console:e2e', 'runtime:lint', 'nexus:typecheck', 'memory:test', 'voice:test', 'files:test'];
  return {
    used: 28,
    total: 30,
    waiting: 18,
    label: 'Test workers',
    runs: 1,
    runLabel: 'verify run',
    holders: held.map(([label, slots], i) => ({ label, slots, pid: RUN, run: 'landing queue', since: since + i * 40 })),
    queue: Array.from({ length: 18 }, (_, i) => ({ label: waiting[i % waiting.length] as string, slots: 4, pid: RUN, run: 'landing queue', since: since + 2_000 + i * 50 })),
    reserve: { slots: 24, inUse: 24, for: 'the landing queue' },
  };
}

/**
 * True when the address asks for the quiet landing queue (?queue=quiet): the demo then has one
 * queue, idle, with only landed history and a report 37 minutes old, as a real queue reads
 * between bursts. Without a window (tests) it is the normal demo.
 */
export function isQuietQueueRequested(): boolean {
  try {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('queue') === 'quiet';
  } catch {
    return false;
  }
}

/** The one queue of the quiet demo: nothing waiting, five landed, no worker busy. */
function quietPipeline(now: number): Pipeline {
  const landed = (n: number, title: string, sha: string, took: number, ago: number): PipelineItem => ({
    id: `1792170${n}00000-6${n}000`,
    title,
    stage: 'landed',
    state: 'passed',
    by: 'lander-idle',
    startedAt: now - ago - took,
    endedAt: now - ago,
    logRef: `land:quiet-${n}`,
    detail: `on main as ${sha}`,
  });
  // Billing-Service on the laptop: a project whose queue Orgus reads from its lander alone (no
  // pipeline.json), so the header shows the lander's own state and how old its report is.
  return {
    projectId: M.billing,
    machineId: M.mbp,
    adapter: 'qawl-land',
    title: 'Landing queue',
    state: 'idle',
    stages: QAWL_STAGES,
    items: [],
    recent: [
      landed(1, 'feat(webhooks): retry failed Stripe webhooks with idempotency keys and a dead letter view (+6)', '2fa7dc8479', 28 * S, 42 * MIN),
      landed(2, 'fix(invoices): backfill the missing September PDFs', '9c1e04b7d2', 34 * S, 58 * MIN),
      landed(3, 'feat(ledger): the March backfill table', '41d8a0c6e3', 31 * S, 1 * H + 16 * MIN),
      landed(4, 'chore(sdk): upgrade the Stripe SDK to v18', '7be22f9a10', 26 * S, 1 * H + 40 * MIN),
      landed(5, 'perf(exports): stream the CSV export', 'c03f5e8b71', 29 * S, 2 * H + 5 * MIN),
    ],
    capacity: { used: 0, total: 30, waiting: 0, label: 'Verify workers' },
    actions: ['pipeline.pause', 'pipeline.resume', 'pipeline.restart'],
    updatedAt: now - 37 * MIN - 47 * S,
  };
}

/**
 * True when the address asks for Orgus's own landings (?queue=landings): the demo then adds the
 * pipeline `orgus land` publishes (pipeline.json through the project protocol), as the live hub
 * shows it: one landing waiting for CPU, one checking, a fresh failure with the whole failing test
 * in its detail, and every CPU slot of the machine busy.
 */
export function isLandingsQueueRequested(): boolean {
  try {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('queue') === 'landings';
  } catch {
    return false;
  }
}

/** Orgus's own landings on studio, from pipeline.json (tools/orgus-cli project-state.ts). */
function landingsPipeline(now: number): Pipeline {
  const done = (n: number, branch: string, state: 'passed' | 'failed', took: number, ago: number, detail: string, tags?: string[]): PipelineItem => ({
    id: `land-${now - ago}-${n}`,
    title: branch,
    stage: state === 'passed' ? 'landed' : ORGUS_LAND_STAGE,
    state,
    startedAt: now - ago - took,
    endedAt: now - ago,
    detail,
    ...(tags ? { tags } : {}),
  });
  return {
    projectId: M.orgus,
    machineId: M.studio,
    adapter: PROJECT_PROTOCOL_ADAPTER,
    title: 'Landings',
    state: 'running',
    stages: [
      { id: 'submitted', title: 'Submitted' },
      { id: 'waiting', title: 'Waiting for CPU' },
      { id: AGENT_CHECKS_STAGE, title: 'Agent checks' },
      { id: ORGUS_LAND_STAGE, title: 'Landing checks' },
      { id: 'landed', title: 'Landed' },
    ],
    items: [
      { id: 'queued-nav-back', title: 'nav-back', stage: 'submitted', state: 'queued', startedAt: now - 15 * S, stageStartedAt: now - 15 * S, detail: 'next to land' },
      { id: 'check-mission-asks', title: 'mission/asks-in-chat', stage: AGENT_CHECKS_STAGE, state: 'active', startedAt: now - 3 * MIN, stageStartedAt: now - 3 * MIN, detail: 'pnpm test:affected, on 12 CPU slots (agent checking)' },
      { id: 'now-redesign-projects', title: 'redesign-projects', stage: ORGUS_LAND_STAGE, state: 'active', startedAt: now - 70 * S, stageStartedAt: now - 70 * S, detail: '3a5eef16, 12 files changed, on 8 CPU slots (checking)' },
      { id: 'now-sse-revoke', title: 'sse-revoke', stage: 'waiting', state: 'queued', startedAt: now - 25 * S, stageStartedAt: now - 25 * S, detail: '39435b82, 4 files changed, next for CPU (waiting for CPU)' },
    ],
    recent: [
      done(1, 'lane-worktrees', 'failed', 85 * S, 3 * MIN, '2d823c4f; pnpm test:affected failed: contracts/control-plane.test.ts > control plane vs the agent protocol > handles every agent -> hub message type explicitly; stuck: no session to wake (checks failed)', [LANDING_FATE.stuck]),
      done(2, 'stale-holder', 'passed', 84 * S, 6 * MIN, '21e7dd36 Approvals: a launch whose process exited no longer holds its session ids; checked: typecheck (affected), tests related to the change, orgus layout (landed)'),
      done(3, 'nav-short', 'passed', 44 * S, 8 * MIN, '2b445d8f Merge branch main into nav-short; checked: typecheck (affected), orgus layout (landed)'),
      done(4, 'lane-console', 'passed', 11 * MIN, 9 * MIN, 'b7507729 Drawer: a pending tool request shows once; checked: typecheck (affected), orgus layout (landed)'),
      done(5, 'missions-glance', 'passed', 110 * S, 20 * MIN, '56f3b1c1 Missions strip: no empty figures at zero; checked: typecheck (affected), orgus layout (landed)'),
      done(6, 'close-gaps', 'passed', 6 * MIN, 14 * MIN, '901dff83 Tests that failed only on a busy machine hold; checked: pnpm test:affected:full, orgus layout (landed)'),
      done(7, 'mission/landed-closes', 'passed', 3 * MIN, 4 * H, 'd2555dfc A mission whose branch lands closes itself; checked: typecheck (affected), orgus layout (landed)'),
      done(8, 'redesign-projects', 'failed', 2 * S, 2 * MIN, 'Conflicts in apps/web/tests/visual/screenshots/win32/projects-dark.png; back in the queue (conflict)', [LANDING_FATE.returned]),
    ],
    capacity: {
      used: 24,
      total: 24,
      waiting: 1,
      label: 'CPU slots',
      holders: [
        { label: 'redesign-projects', slots: 8, pid: 40112, since: now - 70 * S },
        { label: 'pnpm test (orgus work run)', slots: 12, pid: 40280, since: now - 3 * MIN },
        { label: 'go test ./...', slots: 4, pid: 40410, since: now - 2 * MIN },
      ],
      queue: [{ label: 'sse-revoke', slots: 8, pid: 40501, since: now - 25 * S }],
    },
    actions: [],
    updatedAt: now - 2 * S,
  };
}

function pipelines(now: number): Pipeline[] {
  if (isQuietQueueRequested()) return [quietPipeline(now)];
  // The richer Orgus landings (a fresh failure, every CPU slot busy) in place of the default one.
  if (isLandingsQueueRequested()) return adapterPipelines(now).map((p) => (p.projectId === M.orgus && p.adapter === PROJECT_PROTOCOL_ADAPTER ? landingsPipeline(now) : p));
  return adapterPipelines(now);
}

function adapterPipelines(now: number): Pipeline[] {
  const item = (
    id: string,
    title: string,
    stage: string,
    state: PipelineItem['state'],
    by: string,
    startedAgo?: number,
    endedAgo?: number,
    extra: Partial<PipelineItem> = {},
  ): PipelineItem => ({
    id,
    title,
    stage,
    state,
    by,
    startedAt: startedAgo == null ? undefined : now - startedAgo,
    endedAt: endedAgo == null ? undefined : now - endedAgo,
    logRef: `land:${id}`,
    ...extra,
  });
  return [
    {
      projectId: M.qawl,
      machineId: M.studio,
      adapter: 'qawl-land',
      title: 'Landing queue',
      state: 'running',
      stages: QAWL_STAGES,
      items: [
        // One verify run checks four submissions as one batch: three submitted hours ago, the approvals resubmission (its third attempt) 34 minutes ago, this run 9 minutes old.
        item('1792165281552-44408', 'feat(approvals): the request card in chat, and one inbox', 'verifying', 'active', 'qawl-wt-approvals', 34 * MIN, undefined, { stageStartedAt: now - 9 * MIN, detail: 'with 3 other submissions, on main 4be1f0a2c1' }),
        item('1792165281990-44512', 'fix(chat): keep the draft when a tool call fails', 'verifying', 'active', 'qawl-wt-chat', 2 * H + 41 * MIN, undefined, { stageStartedAt: now - 9 * MIN, detail: 'with 3 other submissions, on main 4be1f0a2c1' }),
        item('1792165282104-44610', 'feat(voice): mint latency in the call panel', 'verifying', 'active', 'qawl-wt-voice', 2 * H + 12 * MIN, undefined, { stageStartedAt: now - 9 * MIN, detail: 'with 3 other submissions, on main 4be1f0a2c1' }),
        item('1792165282331-44702', 'chore(verify): one pool for every checkout', 'verifying', 'active', 'Qawl-Agents', 2 * H + 3 * MIN, undefined, { stageStartedAt: now - 9 * MIN, detail: 'with 3 other submissions, on main 4be1f0a2c1' }),
        item('1792165390117-51022', 'fix(permissions): the grid, as it looked rendered', 'queued', 'queued', 'Qawl-Agents', 2 * MIN, undefined, { priority: true, detail: '1 commit, deploys console' }),
        item('1792165402233-51870', 'feat(memory): the mind panel, phase 1a', 'queued', 'queued', 'qawl-wt-memory', 90 * S, undefined, { detail: '7 commits, deploys console' }),
        item('1792165466809-52311', 'chore(verify): cache the scope check by tree hash', 'queued', 'queued', 'Qawl-Agents', 40 * S, undefined, { detail: '1 commit' }),
      ],
      recent: [
        item('1792161170021-38810', "feat(permissions): the grid's API, at every scope and for each agent", 'landed', 'passed', 'Qawl-Agents', 52 * MIN, 41 * MIN, { detail: 'Deployed runtime and Nexus' }),
        item('1792160550410-37122', 'feat(permissions): the grid, in Capabilities and on each agent', 'landed', 'passed', 'qawl-wt-perms', 66 * MIN, 55 * MIN, { detail: 'Deployed console' }),
        // Two rounds rejected for conflicts before the third attempt (in flight above).
        item('1792161900311-40102', 'feat(approvals): the request card in chat, and one inbox', 'verifying', 'failed', 'land-c9b', 48 * MIN, 36 * MIN, { detail: 'conflicts with main in apps/qawl-server/src/routes/approvals.ts' }),
        item('1792158900144-35120', 'feat(approvals): the request card in chat, and one inbox', 'verifying', 'failed', 'land-a71', 1 * H + 52 * MIN, 1 * H + 38 * MIN, { detail: 'conflicts with main in packages/console/src/chat/stream.tsx' }),
        item('1792159908300-36001', 'feat(browser): connect on demand, with the tab chip', 'verifying', 'failed', 'qawl-wt-browser', 80 * MIN, 72 * MIN, { detail: '2 specs failed in apps/qawl-server' }),
        item('1792158221984-34457', 'fix(chat): a dropped reply reads as interrupted', 'landed', 'passed', 'Qawl-Agents', 2 * H, 110 * MIN, { detail: 'Deployed console' }),
        item('1792157001200-33012', 'feat(files): every format, templated', 'deploying', 'failed', 'qawl-wt-files', 2 * H + 30 * MIN, 2 * H + 18 * MIN, { detail: 'Pages deploy failed: build output empty' }),
        item('1792155800777-31555', 'perf(memory): one query for the brain map', 'landed', 'passed', 'qawl-wt-memory', 3 * H, 2 * H + 49 * MIN, { detail: 'Deployed runtime' }),
        item('1792154600100-30210', 'chore: drop the legacy client sweep', 'queued', 'cancelled', 'Qawl-Agents', 3 * H + 20 * MIN, 3 * H + 18 * MIN, { detail: 'Withdrawn by its author' }),
        item('1792153400555-29004', 'feat(loading): page loader and top progress', 'landed', 'passed', 'qawl-wt-loading', 4 * H, 3 * H + 47 * MIN, { detail: 'Deployed console' }),
      ],
      capacity: qawlPool(now),
      // The lander takes one batch at a time: the queue behind it waits for all four.
      batch: { size: 4, unit: 'submission' },
      actions: ['pipeline.pause', 'pipeline.resume', 'pipeline.restart'],
      updatedAt: now - 2 * S,
    },
    {
      projectId: M.qawl,
      machineId: M.mbp,
      adapter: 'qawl-land',
      title: 'Landing queue',
      state: 'paused',
      stages: QAWL_STAGES,
      items: [item('1792164011000-12001', 'docs(voice): mint latency notes', 'queued', 'queued', 'qawl-agents', 34 * MIN)],
      recent: [item('1792140000000-11000', 'fix(voice): reuse the config fetch for the mint', 'landed', 'passed', 'qawl-agents', 9 * H, 8 * H + 48 * MIN)],
      capacity: { used: 0, total: 2, waiting: 0, label: 'Verify workers' },
      actions: ['pipeline.pause', 'pipeline.resume', 'pipeline.restart'],
      updatedAt: now - 5 * S,
    },
    {
      // Orgus lands itself with orgus land (ADR 0055): agents submit, the lander checks and lands.
      projectId: M.orgus,
      machineId: M.studio,
      adapter: PROJECT_PROTOCOL_ADAPTER,
      title: 'Landings',
      state: 'running',
      stages: [
        { id: 'submitted', title: 'Submitted' },
        { id: 'waiting', title: 'Waiting for CPU' },
        { id: AGENT_CHECKS_STAGE, title: 'Agent checks' },
        { id: ORGUS_LAND_STAGE, title: 'Landing checks' },
        { id: 'landed', title: 'Landed' },
      ],
      items: [
        ...(isHeldLandingRequested() ? [] : [item('now-lane-console', 'lane-console', ORGUS_LAND_STAGE, 'active', 'orgus land', 4 * MIN, undefined, { detail: 'b7507729, 13 files changed, on 8 CPU slots' })]),
        item('check-mission-flow', 'mission/overview-flow', AGENT_CHECKS_STAGE, 'active', 'orgus work run', 2 * MIN, undefined, { detail: 'pnpm test:affected, on 8 CPU slots (agent checking)' }),
        item('now-fair-share', 'fair-share', 'waiting', 'queued', 'orgus land', 2 * MIN, undefined, { detail: 'next for CPU' }),
        item('queued-nav-short', 'nav-short', 'submitted', 'queued', 'orgus land', 1 * MIN, undefined, { detail: 'waited 1 min, next to land' }),
        item('queued-stale-holder', 'stale-holder', 'submitted', 'queued', 'orgus land', 30 * S, undefined, { detail: '1 ahead to land' }),
      ],
      recent: [item('land-1792396252403-5ca1254f', 'efficient-tests', 'landed', 'passed', 'orgus land', 12 * MIN, 9 * MIN, { detail: '5ca1254f' })],
      actions: [],
      updatedAt: now - 2 * S,
      ...(isHeldLandingRequested() ? { hold: 'Main is red: contracts/approvals-web.test.ts > the phone decision is accepted fails. Landings wait until main is fixed.' } : {}),
    },
  ];
}

/** ?landing=held: Orgus's own queue held by a red main (visual tests of the paused queue). */
export function isHeldLandingRequested(): boolean {
  try {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('landing') === 'held';
  } catch {
    return false;
  }
}

function jobs(now: number): Job[] {
  return [
    { id: 'j-verify-1', machineId: M.studio, checkoutId: 'c-qawl-approvals', cwd: 'C:/tmp/qawl-wt-approvals', command: 'npm run verify', state: 'running', startedAt: now - 2 * MIN - 20 * S, endedAt: null, exitCode: null },
    { id: 'j-tc-1', machineId: M.studio, checkoutId: 'c-fd-main', cwd: 'C:/Users/Srika/CascadeProjects/dev-portal', command: 'npm run typecheck', state: 'passed', startedAt: now - 14 * MIN, endedAt: now - 13 * MIN - 38 * S, exitCode: 0 },
    { id: 'j-test-1', machineId: M.mbp, checkoutId: 'c-bill-retry', cwd: '/Users/srikanth/code/billing-service-wt/stripe-retry', command: 'npm test -- webhooks', state: 'failed', startedAt: now - 9 * MIN, endedAt: now - 8 * MIN - 12 * S, exitCode: 1 },
    { id: 'j-build-1', machineId: M.mbp, checkoutId: 'c-docs-mbp', cwd: '/Users/srikanth/code/docs-site', command: 'npm run build', state: 'cancelled', startedAt: now - 40 * MIN, endedAt: now - 39 * MIN, exitCode: null },
  ];
}

const FEED_TEMPLATES: Array<[FeedEventKind, string, Id, Id | null]> = [
  ['subagent.started', 'Subagent started: Review the approvals diff for scope leaks', M.studio, M.qawl],
  ['pipeline.item', 'Verifying: feat(approvals): the request card in chat', M.studio, M.qawl],
  ['session.waiting', 'Needs you: Wire the request card into chat', M.studio, M.qawl],
  ['job', 'npm run verify started in qawl-wt-approvals', M.studio, M.qawl],
  ['subagent.finished', 'Subagent finished: Foundation: protocol, design system, shell', M.studio, M.orgus],
  ['session.started', 'Session started: Generate the API reference from the OpenAPI spec', M.mbp, M.docs],
  ['session.waiting', 'Needs you: Add integration tests for webhook retries', M.mbp, M.billing],
  ['job', 'npm test -- webhooks failed with exit code 1', M.mbp, M.billing],
  ['workflow.started', 'Workflow started: land-and-verify', M.studio, M.qawl],
  ['subagent.finished', 'Subagent failed: Pipelines page', M.studio, M.orgus],
  ['session.started', 'Session started from Orgus: Sessions page', M.studio, M.orgus],
  ['checkout.changed', 'qawl-wt-memory moved to a5c1365 (7 ahead of main)', M.studio, M.qawl],
  ['workflow.finished', 'Workflow finished: memory-research', M.studio, M.qawl],
  ['action', 'Paused the landing queue on mbp-16', M.mbp, M.qawl],
  ['pipeline.item', "Landed: feat(permissions): the grid's API, at every scope", M.studio, M.qawl],
  ['subagent.started', 'Subagent started: Port the request card to the chat stream', M.studio, M.qawl],
  ['session.started', 'Session started: Memory page redesign, phase 1a', M.studio, M.qawl],
  ['pipeline.item', 'Landed: feat(permissions): the grid, in Capabilities', M.studio, M.qawl],
  ['job', 'npm run typecheck passed in dev-portal', M.studio, M.orgus],
  ['pipeline.item', 'Failed verifying: feat(browser): connect on demand', M.studio, M.qawl],
  ['checkout.changed', 'billing-service-wt/stripe-retry has 3 changed files', M.mbp, M.billing],
  ['session.started', 'Session started: Add integration tests for webhook retries', M.mbp, M.billing],
  ['workflow.started', 'Workflow started: orgus-build', M.studio, M.orgus],
  ['session.started', 'Session started: Build Orgus', M.studio, M.orgus],
  ['subagent.finished', 'Subagent finished: Map every caller of requireScope', M.studio, M.qawl],
  ['session.started', 'Session started: Review the artifact lifecycle', M.studio, M.qawl],
  ['pipeline.item', 'Landed: fix(chat): a dropped reply reads as interrupted', M.studio, M.qawl],
  ['session.ended', 'Session ended: Rewrite the getting started guide for v2', M.mbp, M.docs],
  ['pipeline.item', 'Deploy failed: feat(files): every format, templated', M.studio, M.qawl],
  ['subagent.started', 'Subagent started: Map every caller of requireScope', M.studio, M.qawl],
  ['session.started', 'Session started: Approvals, the right person answers', M.studio, M.qawl],
  ['pipeline.item', 'Landed: perf(memory): one query for the brain map', M.studio, M.qawl],
  ['machine.offline', 'build-01 stopped reporting', M.build, null],
  ['session.ended', 'Session ended: Nightly dependency upgrade', M.build, M.billing],
  ['action', 'Started a session on build-01: Nightly dependency upgrade', M.build, M.billing],
  ['pipeline.item', 'Cancelled: chore: drop the legacy client sweep', M.studio, M.qawl],
  ['session.started', 'Session started: Retry failed Stripe webhooks', M.mbp, M.billing],
  ['pipeline.item', 'Landed: feat(loading): page loader and top progress', M.studio, M.qawl],
  ['session.ended', 'Session ended: Investigate voice mint latency', M.mbp, M.qawl],
  ['machine.online', 'mbp-16 connected', M.mbp, null],
];

function feed(now: number, rand: () => number): FeedEvent[] {
  let t = now - 20 * S;
  return FEED_TEMPLATES.map(([kind, title, machineId, projectId], i) => {
    const ev: FeedEvent = { id: `f-seed-${i}`, at: t, kind, title, machineId, projectId };
    t -= Math.round((1 + rand() * 7) * MIN);
    return ev;
  });
}

function audit(now: number): AuditEntry[] {
  const a = (
    i: number,
    ago: number,
    action: ActionKind,
    target: string,
    machineId: Id,
    params: Record<string, unknown>,
    result: AuditEntry['result'] = 'ok',
    message?: string,
  ): AuditEntry => ({ id: `a-seed-${i}`, at: now - ago, actor: 'owner', action, target, machineId, params, result, message });
  return [
    a(1, 26 * MIN, 'session.start', 'orgus: Sessions page', M.studio, { checkoutId: 'c-fd-main', mode: 'headless', permissionMode: 'acceptEdits' }),
    a(2, 34 * MIN, 'pipeline.pause', 'Qawl-Agents landing queue on mbp-16', M.mbp, { projectId: M.qawl }),
    a(3, 41 * MIN, 'job.run', 'npm test -- webhooks', M.mbp, { checkoutId: 'c-bill-retry', command: 'npm test -- webhooks' }),
    a(4, 58 * MIN, 'worktree.remove', 'C:/tmp/qawl-wt-voice-latency', M.studio, { checkoutId: 'c-qawl-voice' }, 'error', 'It has 6 changed files. Commit or discard them first.'),
    a(5, 1 * H + 12 * MIN, 'session.stop', 'Qawl-Agents: Measure the voice mint', M.mbp, { sessionId: '5c2b8e4d-voice-latency' }),
    a(6, 1 * H + 50 * MIN, 'job.cancel', 'npm run build', M.mbp, { jobId: 'j-build-1' }),
    a(7, 2 * H + 40 * MIN, 'worktree.remove', 'C:/tmp/qawl-wt-loading', M.studio, { checkoutId: 'c-qawl-loading' }),
    a(8, 4 * H, 'session.start', 'billing-service: Nightly dependency upgrade', M.build, { checkoutId: 'c-bill-build', mode: 'headless', model: 'claude-haiku-5-5' }),
    a(9, 6 * H, 'project.add', '/Users/srikanth/code/docs-site', M.mbp, { machineId: M.mbp, path: '/Users/srikanth/code/docs-site' }),
    a(10, 26 * H, 'pipeline.restart', 'Qawl-Agents landing queue on studio', M.studio, { projectId: M.qawl }, 'error', 'The lander script does not support --restart.'),
  ];
}

function suggestions(now: number): ProjectSuggestion[] {
  return [
    { machineId: M.mbp, path: '/Users/srikanth/code/infra-terraform', name: 'infra-terraform', remoteUrl: 'git@github.com:acme/infra-terraform.git', lastSessionAt: now - 2 * 24 * H },
    { machineId: M.studio, path: 'C:/Users/Srika/CascadeProjects/scratch', name: 'scratch', remoteUrl: null, lastSessionAt: now - 3 * 24 * H },
  ];
}

/** The full demo aggregate, as GET /api/state would return it. */
export function createMockState(now: number = Date.now()): AggregateState {
  const rand = mulberry32(42);
  const base = { machines: machines(now), projects: projects(), sessions: sessions(now) };
  return {
    at: now,
    machines: base.machines,
    projects: base.projects,
    checkouts: checkouts(now),
    sessions: base.sessions,
    pipelines: pipelines(now),
    jobs: jobs(now),
    feed: feed(now, rand),
    audit: audit(now),
    suggestions: suggestions(now),
    // Module slices the demo has from the first paint (the rest seed themselves where they are read).
    ext: { watch: mockWatch(now), 'machine-load': mockMachineLoad(base, now, M) },
  };
}

/** About seven minutes of load and memory samples per online machine, for sparklines. */
export function createMockHistory(state: AggregateState, now: number = Date.now()): Record<Id, MachineSample[]> {
  const out: Record<Id, MachineSample[]> = {};
  for (const m of state.machines) {
    const rand = mulberry32(m.id.length * 7919 + m.cpus);
    const samples: MachineSample[] = [];
    const n = m.online ? 84 : 24;
    const end = m.online ? now : m.lastSeen;
    let load = m.load;
    let mem = 1 - m.memFree / m.memTotal;
    for (let i = n - 1; i >= 0; i--) {
      samples.unshift({ at: end - (n - 1 - i) * 5 * S, load, mem });
      load = clamp01(load + (rand() - 0.5) * 0.16);
      mem = clamp01(mem + (rand() - 0.5) * 0.02);
    }
    out[m.id] = samples;
  }
  return out;
}

const clamp01 = (v: number) => Math.max(0.02, Math.min(0.98, v));

// ---------------------------------------------------------------------------
// Simulator
// ---------------------------------------------------------------------------

export interface MockSink {
  patch: (p: StatePatch) => void;
  event: (e: FeedEvent) => void;
  audit: (a: AuditEntry) => void;
  getState: () => Entities;
}

const LAST_TEXTS = [
  'Reading the failing spec to see what it expects.',
  'Running the unit tests for the changed package.',
  'The typecheck passes. Moving on to the route handler.',
  'Found the cause: the list query ignores the scope predicate.',
  'Writing a test that reproduces the race first.',
  'Editing three files; the diff stays under 120 lines.',
  'Checking the rendered page at 1440 and 768 px.',
  'Summarising what changed for the commit message.',
];

const NEW_ITEMS = [
  'fix(chat): keep the scroll anchored on new replies',
  'feat(voice): reuse the config fetch for the mint',
  'perf(nexus): batch the usage writes',
  'fix(scope): list predicate from the same grant as the write',
  'feat(notifications): snooze from the reminder',
  'chore(verify): split the slow e2e shard',
];

const NEW_SUBAGENTS: Array<[string, string]> = [
  ['Explore', 'Find every reader of the old config shape'],
  ['code-reviewer', 'Review the diff before landing'],
  ['general-purpose', 'Write the missing empty and error states'],
  ['Plan', 'Plan the migration in safe steps'],
];

let simSeq = 0;
const nextId = (p: string) => `${p}-${Date.now().toString(36)}-${(++simSeq).toString(36)}`;
const pick = <T>(xs: readonly T[]): T | undefined => (xs.length ? xs[Math.floor(Math.random() * xs.length)] : undefined);

function emit(sink: MockSink, kind: FeedEventKind, title: string, machineId: Id, projectId: Id | null, ref?: FeedEvent['ref']) {
  sink.event({ id: nextId('f'), at: Date.now(), kind, title, machineId, projectId, ref });
}

/** One simulated moment: machines report, sessions work, the queue moves. */
export function simulateTick(sink: MockSink): void {
  const st = sink.getState();
  const now = Date.now();
  const upMachines: Machine[] = [];
  const upSessions = new Map<Id, Session>();
  const upPipelines: Pipeline[] = [];
  const upJobs: Job[] = [];

  // Machines report every tick.
  for (const m of Object.values(st.machines)) {
    if (!m.online) continue;
    const load = clamp01(m.load + (Math.random() - 0.5) * 0.14);
    const memUsed = clamp01(1 - m.memFree / m.memTotal + (Math.random() - 0.5) * 0.02);
    upMachines.push({ ...m, load, memFree: m.memTotal * (1 - memUsed), lastSeen: now });
  }

  const sessions = Object.values(st.sessions);
  const get = (s: Session) => upSessions.get(s.id) ?? s;

  // Working sessions make progress.
  for (const s of sessions.filter((x) => x.status === 'working')) {
    const tc = 1 + Math.floor(Math.random() * 3);
    upSessions.set(s.id, {
      ...get(s),
      lastActivity: now,
      toolCalls: s.toolCalls + tc,
      tokens: { input: s.tokens.input + tc * 3_100, output: s.tokens.output + tc * 420, cacheRead: s.tokens.cacheRead + tc * 21_000 },
      lastText: Math.random() < 0.35 ? (pick(LAST_TEXTS) ?? s.lastText) : s.lastText,
    });
  }

  const r = Math.random();
  if (r < 0.22) {
    // A session changes status.
    // The demo questions (ADR 0056) hold still: they show the asked, open and dismissed states.
    const live = sessions.filter((x) => x.status !== 'ended' && !x.ext?.asks);
    const s = pick(live);
    if (s) {
      const cur = get(s);
      const next: SessionStatus =
        cur.status === 'working' ? (Math.random() < 0.5 ? 'waiting' : 'idle') : 'working';
      // A session that carries its adapter's canonical state moves it with its status.
      const state = cur.state ? (next === 'working' ? 'working' : next === 'waiting' ? 'waiting_for_human' : 'idle') : undefined;
      upSessions.set(s.id, { ...cur, status: next, ...(state ? { state } : {}), lastActivity: now, turns: next === 'working' ? cur.turns + 1 : cur.turns });
      if (next === 'waiting') emit(sink, 'session.waiting', `Needs you: ${clipTitle(cur.title)}`, cur.machineId, cur.projectId, { type: 'session', id: cur.id });
    }
  } else if (r < 0.5) {
    // The landing queue moves.
    const p = Object.values(st.pipelines).find((x) => x.state === 'running');
    if (p) upPipelines.push(advancePipeline(sink, p, now));
  } else if (r < 0.68) {
    // A subagent finishes, or a working session starts one.
    const withRunning = sessions.filter((x) => x.subagents.some((a) => a.status === 'running'));
    const s = Math.random() < 0.6 ? pick(withRunning) : pick(sessions.filter((x) => x.status === 'working'));
    if (s) {
      const cur = get(s);
      const running = cur.subagents.filter((a) => a.status === 'running');
      if (running.length && Math.random() < 0.6) {
        const a = pick(running)!;
        const failed = Math.random() < 0.12;
        const done: Subagent = { ...a, status: failed ? 'failed' : 'done', endedAt: now, lastText: failed ? 'Stopped: a tool call failed three times.' : 'Finished; summary returned to the session.' };
        upSessions.set(cur.id, { ...cur, subagents: cur.subagents.map((x) => (x.id === a.id ? done : x)) });
        emit(sink, 'subagent.finished', `${failed ? 'Subagent failed' : 'Subagent finished'}: ${a.description}`, cur.machineId, cur.projectId, { type: 'session', id: cur.id });
      } else if (cur.status === 'working' && agentSupports(cur.agentKind, 'subagents') && cur.subagents.filter((a) => a.status === 'running').length < 4) {
        const [type, description] = pick(NEW_SUBAGENTS) ?? ['Explore', 'Look around'];
        const a: Subagent = { id: nextId(`${cur.id}-a`), sessionId: cur.id, type, description, status: 'running', startedAt: now, endedAt: null, toolCalls: 0, lastText: 'Starting.' };
        upSessions.set(cur.id, { ...cur, subagents: [...cur.subagents, a] });
        emit(sink, 'subagent.started', `Subagent started: ${description}`, cur.machineId, cur.projectId, { type: 'session', id: cur.id });
      }
    }
  } else if (r < 0.82) {
    // A workflow makes progress.
    const s = pick(sessions.filter((x) => x.workflows.some((w) => w.status === 'running')));
    if (s) {
      const cur = get(s);
      const workflows = cur.workflows.map((w): WorkflowRun => {
        if (w.status !== 'running') return w;
        const phases = w.phases.map((ph) => ({ ...ph }));
        const ph = phases.find((x) => x.done < x.agents);
        if (ph) ph.done++;
        const finished = phases.every((x) => x.done >= x.agents);
        if (finished) emit(sink, 'workflow.finished', `Workflow finished: ${w.name}`, cur.machineId, cur.projectId, { type: 'session', id: cur.id });
        return finished ? { ...w, phases, status: 'done', endedAt: now } : { ...w, phases };
      });
      upSessions.set(cur.id, { ...cur, workflows });
    }
  } else {
    // Running subagents report tool calls; a running job may finish.
    for (const s of sessions) {
      if (!s.subagents.some((a) => a.status === 'running')) continue;
      const cur = get(s);
      upSessions.set(cur.id, {
        ...cur,
        subagents: cur.subagents.map((a) => (a.status === 'running' ? { ...a, toolCalls: a.toolCalls + 1 + Math.floor(Math.random() * 2) } : a)),
      });
    }
    const j = Object.values(st.jobs).find((x) => x.state === 'running' && now - x.startedAt > 3 * MIN);
    if (j) {
      const ok = Math.random() < 0.8;
      upJobs.push({ ...j, state: ok ? 'passed' : 'failed', endedAt: now, exitCode: ok ? 0 : 1 });
      emit(sink, 'job', `${j.command} ${ok ? 'passed' : 'failed with exit code 1'}`, j.machineId, null, { type: 'job', id: j.id });
    }
  }

  sink.patch({
    at: now,
    upserts: {
      machines: upMachines,
      sessions: [...upSessions.values()],
      ...(upPipelines.length ? { pipelines: upPipelines } : {}),
      ...(upJobs.length ? { jobs: upJobs } : {}),
    },
    removes: {},
  });
}

const clipTitle = (t: string) => (t.length > 60 ? `${t.slice(0, 59).trimEnd()}…` : t);

function advancePipeline(sink: MockSink, p: Pipeline, now: number): Pipeline {
  // An agent's own checks (ORGU-83) run in its worktree beside the queue: they are not a landing,
  // so the one-landing-at-a-time bookkeeping below leaves them out, and a landing never visits
  // that stage.
  const isLanding = (i: PipelineItem) => i.stage !== AGENT_CHECKS_STAGE;
  const order = p.stages.map((s) => s.id).filter((s) => s !== AGENT_CHECKS_STAGE);
  let items = [...p.items];
  let recent = [...p.recent];
  const active = items.find((i) => i.state === 'active' && isLanding(i));
  const finish = (it: PipelineItem, state: 'passed' | 'failed', detail: string) => {
    items = items.filter((x) => x.id !== it.id);
    recent = [{ ...it, state, endedAt: now, detail }, ...recent].slice(0, 20);
  };

  if (active) {
    const idx = order.indexOf(active.stage);
    if (active.stage === 'verifying' && Math.random() < 0.15) {
      finish(active, 'failed', '1 spec failed in apps/qawl-server');
      emit(sink, 'pipeline.item', `Failed verifying: ${active.title}`, p.machineId, p.projectId, { type: 'pipelineItem', id: active.id });
    } else if (idx >= order.length - 2) {
      finish({ ...active, stage: order[order.length - 1] ?? active.stage }, 'passed', 'Deployed');
      emit(sink, 'pipeline.item', `Landed: ${active.title}`, p.machineId, p.projectId, { type: 'pipelineItem', id: active.id });
    } else {
      const stage = order[idx + 1] ?? active.stage;
      items = items.map((x) => (x.id === active.id ? { ...x, stage, stageStartedAt: now } : x));
      const title = p.stages.find((s) => s.id === stage)?.title ?? stage;
      emit(sink, 'pipeline.item', `${title}: ${active.title}`, p.machineId, p.projectId, { type: 'pipelineItem', id: active.id });
    }
  }
  // An agent whose checks pass submits its branch: the item joins the queue's first stage.
  for (const check of items.filter((i) => i.state === 'active' && !isLanding(i))) {
    if (Math.random() < 0.05) {
      items = items.map((x) => (x.id === check.id ? { ...x, stage: order[0] ?? x.stage, state: 'queued', stageStartedAt: now, detail: 'checks passed, submitted' } : x));
      emit(sink, 'pipeline.item', `Submitted: ${check.title}`, p.machineId, p.projectId, { type: 'pipelineItem', id: check.id });
    }
  }
  if (!items.some((i) => i.state === 'active' && isLanding(i))) {
    const queued = items.filter((i) => i.state === 'queued').sort((a, b) => Number(!!b.priority) - Number(!!a.priority) || (a.startedAt ?? 0) - (b.startedAt ?? 0));
    const next = queued[0];
    if (next) {
      items = items.map((x) => (x.id === next.id ? { ...x, state: 'active', stage: order[1] ?? x.stage, stageStartedAt: now } : x));
      emit(sink, 'pipeline.item', `Verifying: ${next.title}`, p.machineId, p.projectId, { type: 'pipelineItem', id: next.id });
    }
  }
  if (items.length < 3 && Math.random() < 0.6) {
    const title = pick(NEW_ITEMS) ?? 'chore: tidy';
    const id = `${now}-${Math.floor(Math.random() * 90_000 + 10_000)}`;
    items.push({ id, title, stage: order[0] ?? 'queued', state: 'queued', by: 'Qawl-Agents', startedAt: now, logRef: `land:${id}`, detail: '1 commit' });
    emit(sink, 'pipeline.item', `Queued: ${title}`, p.machineId, p.projectId, { type: 'pipelineItem', id });
  }
  const activeCount = items.filter((i) => i.state === 'active').length;
  // A pool that says who holds it stays as it is: random counts would contradict its holders.
  const capacity = p.capacity?.holders
    ? p.capacity
    : p.capacity
    ? { ...p.capacity, used: Math.min(p.capacity.total, activeCount + Math.floor(Math.random() * 3)), waiting: items.filter((i) => i.state === 'queued').length > 2 ? 1 : 0 }
    : undefined;
  // A batch is the items at work in a later stage; with none, the runner waits for the next.
  const inBatch = items.filter((i) => i.stage !== order[0] && i.state !== 'passed' && i.state !== 'failed').length;
  const batch = p.batch && inBatch > 0 ? { ...p.batch, size: inBatch } : undefined;
  return { ...p, items, recent, capacity, batch, updatedAt: now };
}

/** Run the simulator every 1 to 2 s. Returns a stop function. */
export function startMockSimulator(sink: MockSink, opts: { minMs?: number; maxMs?: number } = {}): () => void {
  const min = opts.minMs ?? 1000;
  const max = opts.maxMs ?? 2000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const loop = () => {
    if (stopped) return;
    try {
      simulateTick(sink);
    } catch (e) {
      console.error('Mock simulator tick failed', e);
    }
    timer = setTimeout(loop, min + Math.random() * (max - min));
  };
  timer = setTimeout(loop, min);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

// ---------------------------------------------------------------------------
// Actions in mock mode
// ---------------------------------------------------------------------------

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Handles POST /api/actions without a hub: applies a plausible result and writes audit and feed entries. */
export function createMockActionHandler(sink: MockSink) {
  return async <K extends ActionKind>(machineId: Id, kind: K, params: ActionParamsMap[K]): Promise<ActionResponse> => {
    await delay(500 + Math.random() * 500);
    const st = sink.getState();
    const now = Date.now();
    let res: ActionResponse = { ok: true };
    let target = kind as string;
    const p = params as Record<string, unknown>;
    const machine = st.machines[machineId];
    if (!machine?.online) {
      res = { ok: false, message: `${machine?.name ?? 'That machine'} is offline, so it cannot run this.` };
    } else if (kind === 'session.stop') {
      const s = st.sessions[String(p.sessionId)];
      target = s ? s.title : String(p.sessionId);
      if (s) sink.patch({ at: now, upserts: { sessions: [{ ...s, status: 'ended', pid: null, lastActivity: now }] }, removes: {} });
    } else if (kind === 'worktree.remove') {
      const c = st.checkouts[String(p.checkoutId)];
      target = c?.path ?? String(p.checkoutId);
      if (c && c.dirty > 0) res = { ok: false, message: `It has ${c.dirty} changed files. Commit or discard them first.` };
      else if (c) sink.patch({ at: now, upserts: {}, removes: { checkouts: [c.id] } });
    } else if (kind === 'pipeline.pause' || kind === 'pipeline.resume') {
      const pl = Object.values(st.pipelines).find((x) => x.projectId === p.projectId && x.machineId === machineId);
      if (pl) {
        target = `${pl.title} on ${machine.name}`;
        sink.patch({ at: now, upserts: { pipelines: [{ ...pl, state: kind === 'pipeline.pause' ? 'paused' : 'running', updatedAt: now }] }, removes: {} });
      }
    } else if (kind === 'pipeline.restart') {
      res = { ok: false, message: 'The lander script does not support --restart.' };
    } else if (kind === 'job.run') {
      const job: Job = { id: nextId('j'), machineId, checkoutId: (p.checkoutId as string) ?? null, cwd: String(p.cwd ?? st.checkouts[String(p.checkoutId)]?.path ?? ''), command: String(p.command), state: 'running', startedAt: now, endedAt: null, exitCode: null };
      target = job.command;
      sink.patch({ at: now, upserts: { jobs: [job] }, removes: {} });
      res = { ok: true, data: { jobId: job.id } };
    } else if (kind === 'job.cancel') {
      const j = st.jobs[String(p.jobId)];
      target = j?.command ?? String(p.jobId);
      if (j) sink.patch({ at: now, upserts: { jobs: [{ ...j, state: 'cancelled', endedAt: now }] }, removes: {} });
    } else if (kind === 'session.start') {
      const c = p.checkoutId ? st.checkouts[String(p.checkoutId)] : undefined;
      const id = nextId('s');
      const title = clipTitle(String(p.prompt ?? 'New session'));
      target = title;
      const s: Session = {
        id,
        machineId,
        projectId: c?.projectId ?? null,
        checkoutId: c?.id ?? null,
        cwd: c?.path ?? String(p.cwd ?? ''),
        title,
        status: 'working',
        pid: 40_000 + simSeq,
        model: (p.model as string) ?? 'claude-opus-5',
        permissionMode: (p.permissionMode as string) ?? 'default',
        startedAt: now,
        lastActivity: now,
        turns: 1,
        toolCalls: 0,
        tokens: { input: 0, output: 0, cacheRead: 0 },
        lastText: 'Starting.',
        subagents: [],
        workflows: [],
        origin: 'orgus',
      };
      sink.patch({ at: now, upserts: { sessions: [s] }, removes: {} });
      res = { ok: true, data: { sessionId: id } };
    } else if (kind === 'project.add') {
      target = String(p.path);
    } else if (kind === 'project.remove') {
      target = st.projects[String(p.projectId)]?.name ?? String(p.projectId);
    }
    const entry: AuditEntry = {
      id: nextId('a'),
      at: Date.now(),
      actor: 'owner',
      action: kind,
      target,
      machineId,
      params: p,
      result: res.ok ? 'ok' : 'error',
      message: res.message,
    };
    sink.audit(entry);
    emit(sink, 'action', `${ACTION_LABEL[kind]}${res.ok ? '' : ' failed'}: ${target}`, machineId, null);
    return { ...res, auditId: entry.id };
  };
}

/**
 * True when the page asked for demo data: ?mock=1 (or ?mock=0 to force the hub), remembered
 * for the tab; otherwise the Vite mock mode or VITE_MOCK=1.
 */
export function isMockRequested(): boolean {
  try {
    // A sign-in link means the live hub, even if this tab chose demo data earlier.
    if (/(?:^|[#&])token=/.test(window.location.hash)) {
      sessionStorage.setItem('fd.mock', '0');
      return false;
    }
    const q = new URLSearchParams(window.location.search).get('mock');
    if (q === '1' || q === '0') sessionStorage.setItem('fd.mock', q);
    const stored = sessionStorage.getItem('fd.mock');
    if (stored === '1' || stored === '0') return stored === '1';
  } catch {
    // storage unavailable: fall through to the build flag
  }
  return import.meta.env.MODE === 'mock' || import.meta.env.VITE_MOCK === '1';
}

/** True when ?still=1 asks for demo data that does not change (screenshots and visual tests). */
export function isMockStill(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('still') === '1';
  } catch {
    return false;
  }
}

/** Remember demo or live for this tab (used by the palette's switch). */
export function setMockPreference(on: boolean): void {
  try {
    sessionStorage.setItem('fd.mock', on ? '1' : '0');
  } catch {
    // storage unavailable: applies until reload
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has('mock')) {
    url.searchParams.delete('mock');
    window.history.replaceState(null, '', url.toString());
  }
}

/** Re-exported so tests can key pipelines the same way. */
export { pipelineKey };
